import React, { useContext } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button,
  Box,
  Typography,
  Divider,
  Paper,
  Avatar,
  Chip,
  Alert
} from '@mui/material';
import { 
  Person as PersonIcon, 
  Queue as QueueIcon, 
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
  AdminPanelSettings as AdminIcon,
  Chat as ChatIcon
} from '@mui/icons-material';
import { i18n } from '../../translate/i18n';
import { AuthContext } from '../../context/Auth/AuthContext';

const ShowTicketOpen = ({ isOpen, handleClose, user, queue }) => {
  const { user: currentUser } = useContext(AuthContext);
  const isAdmin = currentUser?.profile === "admin";

  return (
    <Dialog 
      open={isOpen} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        pb: 1,
        backgroundColor: '#f5f5f5',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Avatar sx={{ bgcolor: '#ff9800', width: 32, height: 32 }}>
          <WarningIcon fontSize="small" />
        </Avatar>
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          {i18n.t("showTicketOpenModal.title.header")}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        {(user || queue) && (
          <Box>
            <Typography variant="body1" sx={{ mb: 3, color: '#666', fontSize: '1rem' }}>
              {i18n.t("showTicketOpenModal.form.message")}
            </Typography>
            
            <Paper elevation={1} sx={{ p: 2, backgroundColor: '#fafafa', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Informações do Atendente */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: '#2196f3', width: 28, height: 28 }}>
                    <PersonIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                      {i18n.t("showTicketOpenModal.form.user")}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                      {user || "Atendente não identificado"}
                    </Typography>
                  </Box>
                </Box>
                
                <Divider />
                
                {/* Informações da Fila */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: '#4caf50', width: 28, height: 28 }}>
                    <QueueIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                      {i18n.t("showTicketOpenModal.form.queue")}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                      {queue || "Fila não identificada"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
            
            {/* Instruções para Usuários */}
            <Box sx={{ mt: 3 }}>
              {isAdmin ? (
                <Alert 
                  icon={<AdminIcon />} 
                  severity="info" 
                  sx={{ 
                    backgroundColor: '#e3f2fd', 
                    color: '#1565c0',
                    '& .MuiAlert-icon': { color: '#1565c0' }
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                    <strong>Instruções para Administrador:</strong>
                  </Typography>
                  <Typography variant="body2">
                    • Use o botão <strong>"Ver Todos Contatos"</strong> para localizar este contato<br/>
                    • Você pode visualizar e gerenciar todos os atendimentos em andamento<br/>
                    • Entre em contato com o atendente se necessário
                  </Typography>
                </Alert>
              ) : (
                <Alert 
                  icon={<ChatIcon />} 
                  severity="warning" 
                  sx={{ 
                    backgroundColor: '#fff3e0', 
                    color: '#e65100',
                    '& .MuiAlert-icon': { color: '#e65100' }
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                    <strong>O que fazer:</strong>
                  </Typography>
                  <Typography variant="body2">
                    • Entre em contato com <strong>{user || "o atendente responsável"}</strong> via chat interno<br/>
                    • Coordene o atendimento deste contato diretamente<br/>
                    • Evite duplicar o atendimento
                  </Typography>
                </Alert>
              )}
            </Box>
          </Box>
        )}
        
        {!user && !queue && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Avatar sx={{ bgcolor: '#ff9800', width: 48, height: 48, mx: 'auto', mb: 2 }}>
              <ScheduleIcon />
            </Avatar>
            <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
              {i18n.t("showTicketOpenModal.form.messageWait")}
            </Typography>
            <Chip 
              label="Aguardando" 
              color="warning" 
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          </Box>
        )}
      </DialogContent>
      
      <Divider />
      
      <DialogActions sx={{ p: 2, justifyContent: 'flex-end' }}>
        <Button 
          onClick={handleClose} 
          variant="contained"
          color="primary"
          sx={{ 
            minWidth: 100,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            px: 3
          }}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShowTicketOpen;
